import React, { useState } from 'react';
import { addSourceUrls } from '../../services/sourceService';
import { Alert, Button, Input, Card, Spinner } from '../ui';
import { Plus, Trash2 } from '../ui/icons';

interface AddUrlsFormProps {
  sourceId: string;
  onUrlsAdded?: () => void;
}

const AddUrlsForm: React.FC<AddUrlsFormProps> = ({ sourceId, onUrlsAdded }) => {
  const [newUrl, setNewUrl] = useState<string>('');
  const [urlsToAdd, setUrlsToAdd] = useState<string[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleAddToList = () => {
    if (!newUrl.trim()) return;

    try {
      new URL(newUrl);
      setUrlsToAdd([...urlsToAdd, newUrl.trim()]);
      setNewUrl('');
      setError(null);
    } catch (err) {
      setError('Please enter a valid URL');
    }
  };

  const handleRemoveUrl = (index: number) => {
    setUrlsToAdd(urlsToAdd.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (urlsToAdd.length === 0) {
      setError('Please add at least one URL');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await addSourceUrls(sourceId, urlsToAdd);

      setSuccess(`Successfully added ${urlsToAdd.length} URL${urlsToAdd.length > 1 ? 's' : ''}`);
      setUrlsToAdd([]);

      if (onUrlsAdded) {
        onUrlsAdded();
      }
    } catch (err) {
      console.error('Error adding URLs:', err);
      setError('Failed to add URLs. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddToList();
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Add URLs</h3>

      {error && (
        <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <div className="flex gap-2 mb-4">
        <Input
          label="URL"
          fullWidth
          value={newUrl}
          onChange={(e) => setNewUrl((e.target as HTMLInputElement).value)}
          placeholder="https://example.com/property/123"
          onKeyPress={handleKeyPress}
        />
        <Button
          variant="primary"
          onClick={handleAddToList}
          className="mt-6"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add
        </Button>
      </div>

      {urlsToAdd.length > 0 && (
        <Card className="mb-4 max-h-48 overflow-auto">
          <ul className="divide-y divide-gray-200">
            {urlsToAdd.map((url, index) => (
              <li key={index} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                <span className="text-sm text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap flex-1">
                  {url}
                </span>
                <button
                  onClick={() => handleRemoveUrl(index)}
                  className="ml-4 p-1 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={urlsToAdd.length === 0 || saving}
      >
        {saving && <Spinner size="sm" className="mr-2" />}
        {saving ? 'Saving...' : `Save ${urlsToAdd.length} URL${urlsToAdd.length !== 1 ? 's' : ''}`}
      </Button>
    </div>
  );
};

export default AddUrlsForm;
